import { buildApiUrl } from '@/api/baseUrl'
import { request, ApiError, getAccessToken, notifyAuthExpired, refreshAccessToken } from '@/api/client'
import { randomUUID } from '@/utils/uuid'

export type GenerationStatus =
  | 'created' | 'queued' | 'generating' | 'auditing'
  | 'success' | 'failed' | 'blocked' | 'timeout'

export interface MembershipOffer {
  context_id: string
  surface: 'modal' | 'inline'
  campaign_id: string
  state: 'eligible' | 'impressed' | 'clicked' | 'dismissed'
  expires_at: string
}

export type ReasonCode =
  | 'TXT_RISK_BLOCKED' | 'REF_RISK_BLOCKED' | 'IMG_RISK_BLOCKED'
  | 'TASK_CREATE_FAILED' | 'QUEUE_TIMEOUT' | 'MODEL_ERROR'
  | 'AUDIT_TIMEOUT' | 'AUDIT_ERROR' | 'COMPENSATED_CLOSED'
  | 'REFERENCE_IMAGE_DOWNLOAD_FAILED'
  | 'PROVIDER_BAD_REQUEST' | 'PROVIDER_RATE_LIMITED' | 'PROVIDER_ERROR'
  | 'PROVIDER_TIMEOUT' | 'PROVIDER_CAPACITY'
  | 'PROVIDER_TEXT_POLICY_REJECTED' | 'PROVIDER_SAFETY_BLOCKED'
  | 'PROVIDER_AUTH_FAILED' | 'PROVIDER_INVALID_REQUEST'
  | 'PROVIDER_OUTCOME_UNKNOWN'
  | 'OUTPUT_STORAGE_FAILED' | 'OUTPUT_QUALITY_FAILED'
  | 'TASK_TIMEOUT' | 'RULE_BLOCKED' | 'SAFE_001' | 'SAFE_002'
  | 'GENERATION_FAILED' | 'CONFIG_ERROR'

export interface PromptExpandResponse {
  expanded_prompt: string
  remaining_free_count: number
}

export const expandPrompt = (currentPrompt: string, mode?: string, toolId?: string) =>
  request<PromptExpandResponse>('POST', '/app/generation/prompt-expand', {
    body: { current_prompt: currentPrompt, mode, tool_id: toolId },
  })

export interface GenerationToolOptions {
  restore_strength?: number
  colorize_strength?: number
  outpaint_directions?: Array<'top' | 'bottom' | 'left' | 'right'>
  pose?: 'natural' | 'front' | 'side' | 'cross'
}

// POST /app/generation/create
export interface CreateGenerationRequest {
  prompt: string
  style_id: string
  ratio: string
  reference_image_url?: string
  idempotency_key: string
  model_id?: string
  quality?: string
  template_id?: string
  parent_gen_id?: string
  // v2.0 additions: tool type and generation mode
  tool_id?: string   // "text2img" | "img2img" | "edit" | "restore" | "tryon" | "bg_replace" | etc.
  mode?: string      // "text2img" | "img2img" | "edit" | "restore" | "tryon"
  entry_id?: string  // Frontend entry enum, e.g. "generate_text2img" | "chat_result"
  source_entry?: string // Alias for backend/admin display compatibility
  generation_operation?: 'image_generate' | 'image_edit'
  secondary_image_url?: string  // 试衣第二张图（服装）
  additional_image_urls?: string[] // 多参考图补充输入
  pose?: string                 // 姿势
  tool_options?: GenerationToolOptions
  expected_cost_points?: number
  pricing_config_version?: number
  pricing_config_sha256?: string
}
export interface CreateGenerationResponse {
  gen_id: string
  client_request_id?: string
  status: GenerationStatus
  status_version?: number
  active_count?: number
  active_limit?: number
  points_state?: 'reserved' | 'captured' | 'refunded'
  balance_version?: number
  image_url?: string
  cached?: boolean
  cost_points?: number
  points_cost?: number
  points_balance_after?: number
  poll_timeout_seconds?: number
  reason_code?: ReasonCode
  tool_id?: string
  mode?: string
  entry_id?: string
  source_entry?: string
  generation_operation?: 'image_generate' | 'image_edit'
  progress_stage?: string
  poll_after_ms?: number
  attempt_no?: number
  continues_in_background?: boolean
}
export const createGeneration = (data: CreateGenerationRequest) =>
  request<CreateGenerationResponse>('POST', '/app/generation/create', {
    body: data,
  })

export interface GenerationSubmissionResponse {
  submission_state: 'accepted' | 'not_found' | 'processing'
  client_request_id: string
  gen_id?: string
  status?: GenerationStatus
  status_version?: number
  active_count: number
  active_limit: number
  points_balance: number
  balance_version: number
}

export const getGenerationSubmission = (clientRequestId: string) =>
  request<GenerationSubmissionResponse>(
    'GET',
    `/app/generation/submissions/${encodeURIComponent(clientRequestId)}`,
  )

// GET /app/generation/{genId}
export interface GenerationResult {
  gen_id: string
  status: GenerationStatus
  image_url?: string
  thumbnail_url?: string
  hd_image_url?: string
  can_download_hd: boolean
  points_balance_after?: number
  error_code?: string
  reason_code?: ReasonCode
  failure_stage?: string
  retry_action?: 'none' | 'edit_prompt' | 'resubmit_same_inputs'
  model_id?: string
  quality?: string
  cached?: boolean
  cost_points?: number
  points_cost?: number
  favorited?: boolean
  tool_id?: string
  mode?: string
  entry_id?: string
  source_entry?: string
  generation_operation?: 'image_generate' | 'image_edit'
  progress_stage?: string
  poll_after_ms?: number
  attempt_no?: number
  continues_in_background?: boolean
  status_version?: number
  status_updated_at?: string
  points_state?: 'reserved' | 'captured' | 'refunded'
  fallback_used?: boolean
  membership_offer?: MembershipOffer
}
export const getGeneration = (genId: string) =>
  request<GenerationResult>('GET', `/app/generation/${genId}`)

// GET /app/generation/recent
export interface GenerationRecentItem {
  gen_id: string
  prompt: string
  style_id?: string
  ratio?: string
  model_id: string
  quality: string
  thumbnail_url?: string
  image_url?: string
  created_at: string
  tool_id?: string
  mode?: string
  entry_id?: string
  source_entry?: string
  generation_operation?: 'image_generate' | 'image_edit'
}
export const getRecent = () =>
  request<GenerationRecentItem[]>('GET', '/app/generation/recent')

export interface GenerationPendingItem {
  gen_id: string
  status: GenerationStatus
  prompt: string
  style_id?: string
  ratio?: string
  model_id?: string
  quality?: string
  cost_points?: number
  points_cost?: number
  created_at: string
  tool_id?: string
  mode?: string
  entry_id?: string
  source_entry?: string
  generation_operation?: 'image_generate' | 'image_edit'
}
export const getPendingGenerations = () =>
  request<GenerationPendingItem[]>('GET', '/app/generation/pending')

export interface GenerationTaskSummary {
  gen_id: string
  client_request_id?: string
  status: GenerationStatus
  status_version: number
  status_updated_at: string
  progress_stage: string
  points_state: 'reserved' | 'captured' | 'refunded'
  prompt: string
  created_at: string
  image_url?: string
  thumbnail_url?: string
  reason_code?: ReasonCode
  failure_stage?: string
  retry_action: 'none' | 'edit_prompt' | 'resubmit_same_inputs'
  fallback_used: boolean
  can_retry: boolean
  model_id: string
  quality: string
  ratio: string
  style_id?: string
  mode: string
  tool_id?: string
  entry_id?: string
  points_cost: number
  parent_gen_id?: string
  favorited: boolean
  membership_offer?: MembershipOffer
}

export interface GenerationTasksResponse {
  items: GenerationTaskSummary[]
  next_cursor?: string
  active_count: number
  active_limit: number
  points_balance: number
  balance_version: number
}

export const getGenerationTasks = (
  scope: 'active' | 'recent' | 'needs_action' = 'active',
  updatedAfter?: string,
) => {
  const query = new URLSearchParams({ scope, limit: '50' })
  if (updatedAfter) query.set('updated_after', updatedAfter)
  return request<GenerationTasksResponse>('GET', `/app/generation/tasks?${query.toString()}`)
}

export const recordMembershipOfferEvent = (
  contextId: string,
  event: 'impression' | 'click' | 'dismiss',
  metadata: Record<string, unknown> = {},
) => request<{ context_id: string; state: string }>(
  'POST',
  `/app/growth/offers/${contextId}/events`,
  {
    body: {
      event_id: randomUUID().replaceAll('-', ''),
      event,
      metadata,
    },
  },
)

// PATCH /app/generation/{genId}/favorite
export const toggleFavorite = (genId: string, favorited: boolean) =>
  request<{ gen_id: string; favorited: boolean }>('PATCH', `/app/generation/${genId}/favorite`, {
    body: { favorited },
  })

export const deleteGeneration = (genId: string) =>
  request<null>('DELETE', `/app/generation/${genId}`)

// POST /app/generation/upload-reference
export interface UploadReferenceResponse {
  url: string
  width?: number | null
  height?: number | null
  recommended_ratio?: string | null
}
type ApiEnvelope<T> = {
  success: boolean
  data?: T
  error?: { code?: string; message?: string }
}

async function parseUploadResponse(res: Response): Promise<UploadReferenceResponse> {
  if (res.status === 413) {
    throw new ApiError('UPLOAD_TOO_LARGE', '图片过大，请选择较小的图片', res.status)
  }

  let json: ApiEnvelope<UploadReferenceResponse>
  try {
    json = await res.json()
  } catch {
    throw new ApiError('PARSE_ERROR', 'Failed to parse server response', res.status)
  }

  if (!json.success || !json.data) {
    const error = json.error
    throw new ApiError(
      error?.code ?? 'UNKNOWN_ERROR',
      error?.message ?? 'An unknown error occurred',
      res.status,
    )
  }

  return json.data
}

export const uploadReference = async (file: File): Promise<UploadReferenceResponse> => {
  const executeUpload = (accessToken: string | null) => {
    const form = new FormData()
    form.append('file', file)
    const headers: Record<string, string> = {}
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`

    return fetch(buildApiUrl('/app/generation/upload-reference'), {
      method: 'POST',
      headers,
      body: form,
    })
  }

  let res = await executeUpload(getAccessToken())
  if (res.status === 401) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      res = await executeUpload(newToken)
    } else {
      notifyAuthExpired()
      throw new ApiError('AUTH_001', '登录已过期，请重新登录', 401)
    }
    if (res.status === 401) {
      notifyAuthExpired()
      throw new ApiError('AUTH_001', '登录已过期，请重新登录', 401)
    }
  }

  return parseUploadResponse(res)
}

// POST /app/generation/{genId}/download-url
export interface DownloadUrlResponse { url: string; url_type?: string }
export const getDownloadUrl = (genId: string, quality: 'hd' | 'sd') =>
  request<DownloadUrlResponse>('POST', `/app/generation/${genId}/download-url`, {
    body: { quality },
  })

// GET /app/generation/history
export interface HistoryRecord {
  gen_id: string
  status: GenerationStatus
  image_url?: string
  thumbnail_url?: string
  prompt: string
  style_name: string
  ratio: string
  created_at: string
  points_cost: number
  reason_code?: ReasonCode
  model_id?: string
  quality?: string
  favorited?: boolean
  tool_id?: string
  mode?: string
  entry_id?: string
  source_entry?: string
  generation_operation?: 'image_generate' | 'image_edit'
}
export interface HistoryResponse {
  items: HistoryRecord[]
  total: number
  page: number
  page_size: number
  has_more: boolean
  facets: {
    models: Array<{ model_id: string; count: number }>
  }
}
export interface GenerationHistoryFilters {
  statusGroup?: 'active' | 'success' | 'unsuccessful'
  modelId?: string
  createdFrom?: string
  sortOrder?: 'asc' | 'desc'
}
export const getHistory = (
  page = 1,
  pageSize = 20,
  favoritedOnly?: boolean,
  filters: GenerationHistoryFilters = {},
) => {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
  if (favoritedOnly) params.set('favorited', 'true')
  if (filters.statusGroup) params.set('status_group', filters.statusGroup)
  if (filters.modelId) params.set('model_id', filters.modelId)
  if (filters.createdFrom) params.set('created_from', filters.createdFrom)
  if (filters.sortOrder) params.set('sort_order', filters.sortOrder)
  return request<HistoryResponse>('GET', `/app/generation/history?${params}`)
}

// GET /app/generation/image-proxy — fetch OSS image via backend proxy (bypasses browser CORS)
export async function fetchImageProxy(imageUrl: string): Promise<Blob> {
  const token = getAccessToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`
  const response = await fetch(
    buildApiUrl(`/app/generation/image-proxy?url=${encodeURIComponent(imageUrl)}`),
    { headers },
  )
  if (!response.ok) throw new Error('image proxy fetch failed')
  return response.blob()
}

const TERMINAL_STATUSES: GenerationStatus[] = ['success', 'failed', 'blocked', 'timeout']

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const waitUntilVisible = () => new Promise<void>((resolve) => {
  if (typeof document === 'undefined' || document.visibilityState === 'visible') {
    resolve()
    return
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      resolve()
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

const isRetryablePollError = (err: unknown): boolean => {
  if (!(err instanceof ApiError)) return true
  return err.status === 0 || err.status >= 500 || err.code === 'PARSE_ERROR'
}

// Polls every 2s while the app is active. Native/webview background pauses and
// transient network failures should not turn an in-flight server task into a UI failure.
export async function pollGeneration(genId: string, pollTimeoutSeconds?: number): Promise<GenerationResult> {
  const maxActivePollMs = pollTimeoutSeconds && pollTimeoutSeconds > 0
    ? pollTimeoutSeconds * 1000
    : 360_000
  let pollIntervalMs = 2_000
  let activePollMs = 0

  while (activePollMs < maxActivePollMs) {
    await waitUntilVisible()

    try {
      const result = await getGeneration(genId)
      if (TERMINAL_STATUSES.includes(result.status)) {
        return result
      }
      if (result.poll_after_ms && result.poll_after_ms >= 500) {
        pollIntervalMs = Math.min(result.poll_after_ms, 10_000)
      }
    } catch (err) {
      if (!isRetryablePollError(err)) throw err
    }

    await sleep(pollIntervalMs)
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      activePollMs += pollIntervalMs
    }
  }

  throw new ApiError('GEN_TIMEOUT', '生成超时，请重试', 408)
}
